/**
 * Banner 樣式相關工具函數
 * 統一管理 EditableBanner 和 TeacherPublicPage 的 Banner 渲染邏輯
 */

/**
 * 獲取 Banner 高度
 * @param {Object} pageDesign - 頁面設計配置
 * @param {number} defaultHeight - 默認高度（像素）
 * @returns {number} Banner 高度
 */
export function getBannerHeight(pageDesign, defaultHeight = 200) {
  if (!pageDesign?.banner) return defaultHeight;
  return pageDesign.banner.height || defaultHeight;
}

/**
 * 應用寬度模式樣式
 * @param {string} widthMode - 寬度模式 ('content' | 'full')
 * @returns {Object} CSS 樣式對象
 */
function applyWidthMode(widthMode) {
  if (widthMode === 'content') {
    return {
      maxWidth: '42rem',
      margin: '0 auto'
    };
  } else if (widthMode === 'full') {
    // Breakout of parent container to achieve full viewport width
    return {
      width: '100vw',
      marginLeft: 'calc(-50vw + 50%)',
      marginRight: 'calc(-50vw + 50%)'
    };
  }
  return {};
}

/**
 * 應用圓角樣式
 * @param {string} borderRadius - 圓角模式 ('none' | 'top' | 'all')
 * @returns {Object} CSS 樣式對象
 */
function applyBorderRadius(borderRadius) {
  if (borderRadius === 'none') {
    return { borderRadius: '0' };
  } else if (borderRadius === 'top') {
    return { borderRadius: '1rem 1rem 0 0' };
  } else if (borderRadius === 'all') {
    return { borderRadius: '1rem' };
  }
  return {};
}

/**
 * 獲取 Banner 背景樣式（根據類型）
 * @param {Object} banner - Banner 配置對象
 * @returns {Object} CSS 樣式對象
 */
function getBannerBackground(banner) {
  if (banner.type === 'color') {
    return { backgroundColor: banner.color || '#3366FF' };
  } else if (banner.type === 'gradient') {
    return {
      background: `linear-gradient(135deg, ${banner.gradient_start || '#6699FF'}, ${banner.gradient_end || '#3366FF'})`
    };
  } else if (banner.type === 'custom' && banner.image_url) {
    return {
      backgroundImage: `url(${banner.image_url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    };
  }
  return {};
}

/**
 * 獲取 Banner 完整樣式
 * @param {Object} pageDesign - 頁面設計配置
 * @param {Object} options - 配置選項
 * @param {number} options.defaultHeight - 默認高度（默認 200）
 * @param {Object} options.additionalStyles - 額外樣式（如編輯器特定樣式）
 * @param {boolean} options.returnNullIfNoBanner - 無 banner 時返回 null（默認 false）
 * @returns {Object|null} CSS 樣式對象或 null
 */
export function getBannerStyle(pageDesign, options = {}) {
  const {
    defaultHeight = 200,
    additionalStyles = {},
    returnNullIfNoBanner = false
  } = options;

  const banner = pageDesign?.banner;

  // 無 banner 時的處理
  if (!banner) {
    if (returnNullIfNoBanner) {
      return null;
    }
    // 返回默認漸層
    return {
      height: `${defaultHeight}px`,
      background: 'linear-gradient(135deg, #6699FF, #3366FF)',
      ...additionalStyles
    };
  }

  // 處理高度（支援字串和數字）
  const height = banner.height || defaultHeight;
  const heightValue = typeof height === 'string' ? height : `${height}px`;

  // 寬度模式和圓角
  const widthMode = banner.width_mode || 'full';
  const borderRadius = banner.border_radius || 'top';

  // 組合基礎樣式
  const baseStyle = {
    height: heightValue,
    ...applyWidthMode(widthMode),
    ...applyBorderRadius(borderRadius),
    ...additionalStyles
  };

  // 應用背景樣式
  const backgroundStyle = getBannerBackground(banner);

  return {
    ...baseStyle,
    ...backgroundStyle
  };
}

/**
 * 默認的編輯器樣式（用於 EditableBanner）
 */
export const EDITOR_BANNER_STYLES = {
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.2s'
};
